// client/src/hooks/useAuth.js
import React, { useCallback } from 'react';

export const useAuth = () => {
    const [token, setToken] = React.useState(localStorage.getItem('jwtToken'));
    const [user, setUser] = React.useState(null);
    const [error, setError] = React.useState('');
    const [loading, setLoading] = React.useState(false);

    const logout = useCallback(() => {
        localStorage.removeItem('jwtToken');
        setToken(null);
        setUser(null);
        // Redirect to login page to prevent being stuck on a protected page
        window.location.href = '/portal';
    }, []);

    React.useEffect(() => {
        const storedToken = localStorage.getItem('jwtToken');
        if (storedToken) {
            setToken(storedToken);
            try {
                const decoded = JSON.parse(atob(storedToken.split('.')[1]));
                // Check if token is expired
                if (decoded.exp * 1000 < Date.now()) {
                    logout();
                } else {
                    setUser(decoded);
                }
            } catch (e) {
                console.error("Failed to decode token:", e);
                logout();
            }
        }
    }, [logout]); // logout is a stable useCallback([]), so this still runs once on mount.

    // Two-factor: after a correct password on an account with an authenticator,
    // the server answers with a short-lived challenge instead of a session. It is
    // kept in memory only — never in localStorage — and is useless as a token.
    const [challenge, setChallenge] = React.useState(null);
    const [notice, setNotice] = React.useState('');

    const adoptSession = (data) => {
        localStorage.setItem('jwtToken', data.token);
        setToken(data.token);
        const decoded = JSON.parse(atob(data.token.split('.')[1]));
        setUser(decoded);
    };

    const login = async (username, password) => {
        setLoading(true);
        setError('');
        setNotice('');
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Login failed');
            if (data.twoFactorRequired) {
                setChallenge({ token: data.challengeToken, authenticatorAvailable: data.authenticatorAvailable !== false });
                return;
            }
            adoptSession(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // body: { code } or { recoveryCode }
    const verifySecondFactor = async (body) => {
        if (!challenge) return;
        setLoading(true);
        setError('');
        try {
            const response = await fetch('/api/auth/2fa/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeToken: challenge.token, ...body }),
            });
            const data = await response.json();
            if (!response.ok) {
                if (data.restart) setChallenge(null);
                throw new Error(data.message || 'Verification failed');
            }
            setChallenge(null);
            if (data.notice) {
                setNotice(data.notice);
                try { sessionStorage.setItem('twoFactorNotice', data.notice); } catch (e) { /* ignore */ }
            }
            adoptSession(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const cancelSecondFactor = () => { setChallenge(null); setError(''); };

    // Password-free sign-in (passkey or trusted Tailscale device) hands over the same
    // session the password door does.
    const adoptPasswordlessSession = (data) => { setError(''); setChallenge(null); adoptSession(data); };

    return { token, user, login, logout, error, loading,
             challenge, verifySecondFactor, cancelSecondFactor, notice, adoptPasswordlessSession };
};